import React, { useState, useEffect } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { FormBuilder, Form } from "@formio/react";
import axios from "axios";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {}
  render() {
    if (this.state.hasError) {
      return <div style={{color:'red',padding:'1rem'}}>Form builder error: {this.state.error?.message || 'Unknown error'}</div>;
    }
    return this.props.children;
  }
}

export default function DynamicFormBuilder() {
  // Disable Ace worker for JSON mode to prevent importScripts error
  useEffect(() => {
    if (window.ace && window.ace.config) {
      window.ace.config.set('workerPath', '/ace');
    }
    // If you use Ace editor instance directly, disable worker for JSON mode
    if (window.ace && window.ace.edit) {
      const editor = window.ace.edit('ace-editor');
      if (editor && editor.session && editor.session.setUseWorker) {
        editor.session.setUseWorker(false);
      }
    }
  }, []);
  // Form.io state
  const [formSchema, setFormSchema] = useState({
    display: 'form',
    components: []
  });
  const [formData, setFormData] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [formName, setFormName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  // Build List array from panels/groups and radio/checkbox fields
  const handleSaveForm = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem('token');

      // Build List array
      const List = [];

      // New grouping logic: group fields under last htmlelement header, do not create section for N/A checkbox alone
      let currentHeader = '';
      let currentItems = [];
      const pushSection = () => {
        if (currentItems.length > 0) {
          List.push({ header: currentHeader, items: currentItems });
          currentItems = [];
        }
      };
      formSchema.components.forEach(comp => {
        if (comp.type === 'htmlelement') {
          pushSection();
          currentHeader = comp.content || comp.label || '';
        } else if (comp.type === 'columns' && Array.isArray(comp.columns)) {
          let colHasHtml = false;
          let colHasTextarea = false;
          comp.columns.forEach(col => {
            if ((col.components || []).some(c => c.type === 'htmlelement')) colHasHtml = true;
            if ((col.components || []).some(c => c.type === 'textarea')) colHasTextarea = true;
          });
          comp.columns.forEach(col => {
            // Find all checkboxes in this column
            const checkboxes = (col.components || []).filter(c => c.type === 'checkbox' && c.label && c.key);
            if (checkboxes.length > 0) {
              const allLabels = checkboxes.map(cb => cb.label);
              const checkedLabels = checkboxes.filter(cb => formData[cb.key]).map(cb => cb.label);
              if (colHasHtml && colHasTextarea) {
                pushSection();
                currentHeader = comp.columns.find(c => (c.components || []).some(cc => cc.type === 'htmlelement'))?.components.find(cc => cc.type === 'htmlelement')?.content || '';
              }
              currentItems.push({
                title: '',
                options: allLabels,
                value: checkedLabels,
                type: 'checkbox'
              });
            }
            (col.components || []).forEach(colComp => {
              if (colComp.type === 'signature') {
                let value = formData[colComp.key] !== undefined ? formData[colComp.key] : '';
                currentItems.push({
                  title: colComp.label || colComp.key,
                  options: [],
                  value,
                  type: colComp.type,
                  isSignature: true
                });
              } else if (colComp.type === 'htmlelement') {
                if (!currentHeader) {
                  currentHeader = colComp.content || colComp.label || '';
                }
              } else if (colComp.type === 'radio') {
                currentItems.push({
                  title: colComp.label || colComp.key,
                  options: colComp.values ? colComp.values.map(v => v.value) : [],
                  value: formData[colComp.key] || '',
                  type: colComp.type
                });
              } else if (colComp.type === 'textarea') {
                let value = formData[colComp.key] !== undefined ? formData[colComp.key] : '';
                currentItems.push({
                  title: colComp.label || colComp.key,
                  options: [],
                  value,
                  type: colComp.type
                });
              } else if (colComp.type === 'textfield' || colComp.type === 'date' || colComp.type === 'time' || colComp.type === 'datetime') {
                let value = formData[colComp.key] !== undefined ? formData[colComp.key] : '';
                if ((colComp.label || colComp.key).toLowerCase() === 'date' && typeof value === 'string' && value.includes('T')) {
                  const d = new Date(value);
                  value = d && !isNaN(d) ? `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}` : value;
                } else if ((colComp.label || colComp.key).toLowerCase().includes('time') && typeof value === 'string' && value.includes('T')) {
                  const match = value.match(/T(\d{2}:\d{2})/);
                  value = match ? match[1] : value;
                }
                currentItems.push({
                  title: colComp.label || colComp.key,
                  options: [],
                  value,
                  type: colComp.type
                });
              }
            });
          });
        } else if (comp.type === 'radio') {
          currentItems.push({
            title: comp.label || comp.key,
            options: comp.values ? comp.values.map(v => v.value) : [],
            value: formData[comp.key] || '',
            type: comp.type
          });
        } else if (comp.type === 'textarea') {
          let value = formData[comp.key] !== undefined ? formData[comp.key] : '';
          currentItems.push({
            title: comp.label || comp.key,
            options: [],
            value,
            type: comp.type
          });
        } else if (comp.type === 'textfield' || comp.type === 'date' || comp.type === 'time' || comp.type === 'datetime') {
          let value = formData[comp.key] !== undefined ? formData[comp.key] : '';
          if ((comp.label || comp.key).toLowerCase() === 'date' && typeof value === 'string' && value.includes('T')) {
            const d = new Date(value);
            value = d && !isNaN(d) ? `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}` : value;
          } else if ((comp.label || comp.key).toLowerCase().includes('time') && typeof value === 'string' && value.includes('T')) {
            const match = value.match(/T(\d{2}:\d{2})/);
            value = match ? match[1] : value;
          }
          currentItems.push({
            title: comp.label || comp.key,
            options: [],
            value,
            type: comp.type
          });
        } else if (comp.type === 'checkbox') {
          currentItems.push({
            title: comp.label || comp.key,
            options: [comp.label || comp.key],
            value: !!formData[comp.key],
            type: comp.type
          });
        }
      });
      pushSection();
      // Remove any section with empty items
      const filteredList = List.filter(section => section.items.length > 0);

      // After pushSection(), group checkbox items in each section
      const groupCheckboxItems = (list) => {
        return list.map(section => {
          // Find all checkbox items (options length 1, value is boolean)
          const checkboxItems = section.items.filter(item => Array.isArray(item.options) && item.options.length === 1 && typeof item.value === 'boolean');
          const otherItems = section.items.filter(item => !(Array.isArray(item.options) && item.options.length === 1 && typeof item.value === 'boolean'));
          if (checkboxItems.length > 0) {
            return {
              header: section.header,
              items: [
                ...otherItems,
                {
                  title: '',
                  options: checkboxItems.map(item => item.options[0]),
                  value: checkboxItems.filter(item => item.value === true).map(item => item.options[0])
                }
              ]
            };
          }
          return section;
        });
      };
      const finalList = groupCheckboxItems(filteredList);

      // Exclude radio/checkbox and submit fields from top-level payload
      // Remove radio/checkbox and paired textfield keys from top-level payload
      const radioCheckboxKeys = formSchema.components
        .filter(comp => comp.type === 'radio' || comp.type === 'checkbox')
        .map(comp => comp.key)
        .filter(Boolean);
      // Also remove textfield keys paired with checkbox in columns
      const pairedTextCheckboxKeys = [];
      formSchema.components.forEach(comp => {
        if (comp.type === 'columns' && Array.isArray(comp.columns)) {
          let textComp = null;
          let checkboxComp = null;
          comp.columns.forEach(col => {
            (col.components || []).forEach(colComp => {
              if (colComp.type === 'textfield') textComp = colComp;
              if (colComp.type === 'checkbox') checkboxComp = colComp;
            });
          });
          if (textComp && checkboxComp) {
            pairedTextCheckboxKeys.push(textComp.key);
            pairedTextCheckboxKeys.push(checkboxComp.key);
          }
        }
      });
        // Remove checkbox fields from top-level payload, including nested columns
        const { List: _ignore, submit, ...otherFields } = formData;
        function removeCheckboxKeysAndColumnPairs(components) {
          components.forEach(comp => {
            if (comp.type === 'checkbox' && comp.key && otherFields.hasOwnProperty(comp.key)) {
              delete otherFields[comp.key];
            } else if (comp.type === 'columns' && Array.isArray(comp.columns)) {
              // Collect all textfield and checkbox keys in all columns of this Columns component
              let textKeys = [];
              let checkboxKeys = [];
              comp.columns.forEach(col => {
                (col.components || []).forEach(colComp => {
                  if (colComp.type === 'textfield') textKeys.push(colComp.key);
                  if (colComp.type === 'checkbox') checkboxKeys.push(colComp.key);
                });
                if (Array.isArray(col.components)) {
                  removeCheckboxKeysAndColumnPairs(col.components);
                }
              });
              // If both a textfield and a checkbox exist in any columns, remove both from top-level
              if (textKeys.length > 0 && checkboxKeys.length > 0) {
                textKeys.forEach(key => { if (otherFields.hasOwnProperty(key)) delete otherFields[key]; });
                checkboxKeys.forEach(key => { if (otherFields.hasOwnProperty(key)) delete otherFields[key]; });
              }
            }
          });
        }
        removeCheckboxKeysAndColumnPairs(formSchema.components);
  // Do not remove radio/checkbox/text fields from top-level payload
        // Remove 'experiencedAtWorkingAtHeight' and 'checkbox' from top-level payload, keep only in List
        const { experiencedAtWorkingAtHeight, checkbox, ...filteredFields } = otherFields;

        // Map textfield key to its label in the payload, preserving spaces and case
        let textFieldLabelValue = {};
        // Move confirmation radio field into List array if present
        const confirmKey = Object.keys(filteredFields).find(k => k.toLowerCase().includes('iconfirmtheabovechecks'));
        // Do not statically push confirmation radio field into List array; keep logic dynamic
        if (confirmKey) {
          delete filteredFields[confirmKey];
        }
        // Remove radio, checkbox, and default 'textField' fields from top-level payload
        Object.keys(filteredFields).forEach(key => {
          // Remove any radio or checkbox value from top-level payload
          const isRadio = formSchema.components.some(comp => comp.key === key && comp.type === 'radio');
          const isCheckbox = formSchema.components.some(comp => comp.key === key && comp.type === 'checkbox');
          if (isRadio || isCheckbox) {
            delete filteredFields[key];
          }
        });
        // Map textfields at root and inside columns to their label in the payload

        // Only allow system fields and List in payload
        // Add date/time component keys to allowedKeys
        const dateTimeKeys = formSchema.components
          .filter(comp => comp.type === 'datetime' || comp.type === 'date' || comp.type === 'time')
          .map(comp => comp.key)
          .filter(Boolean);
        const allowedKeys = ['_id', 'formName', 'List', 'createdAt', 'updatedAt', '__v', ...dateTimeKeys];
        const payload = {};
        allowedKeys.forEach(key => {
          if (typeof filteredFields[key] !== 'undefined') {
            const comp = formSchema.components.find(c => c.key === key);
            // Date-only field: strip time part
            if (comp && (comp.type === 'date' || comp.type === 'datetime') && comp.enableTime === false && typeof filteredFields[key] === 'string') {
              const dateOnly = filteredFields[key].split('T')[0];
              payload[key] = dateOnly;
            }
            // Time-only field: extract only time part (for datetime with only time enabled)
            else if (comp && comp.type === 'datetime' && comp.enableDate === false && comp.enableTime === true && typeof filteredFields[key] === 'string') {
              let timeStr = filteredFields[key];
              if (/T\d{2}:\d{2}:\d{2}/.test(timeStr)) {
                const match = timeStr.match(/T(\d{2}:\d{2}:\d{2})/);
                timeStr = match ? match[1] : timeStr;
              } else if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(timeStr)) {
                timeStr = timeStr.split(' ')[1];
              }
              payload[key] = timeStr;
            }
            // Legacy time-only field
            else if (comp && comp.type === 'time' && typeof filteredFields[key] === 'string') {
              let timeStr = filteredFields[key];
              if (/T\d{2}:\d{2}:\d{2}/.test(timeStr)) {
                const match = timeStr.match(/T(\d{2}:\d{2}:\d{2})/);
                timeStr = match ? match[1] : timeStr;
              } else if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(timeStr)) {
                timeStr = timeStr.split(' ')[1];
              }
              payload[key] = timeStr;
            }
            else {
              payload[key] = filteredFields[key];
            }
          }
        });
        payload.formName = formName;
        payload.List = finalList;

      const res = await axios.post(
        `${baseUrl}/api/dynamic-forms`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        setSaveMsg("Form saved successfully!");
        setFormData({}); // Clear form data only after successful save
      } else {
        setSaveMsg(res.data.message || "Failed to save form.");
      }
    } catch (err) {
      setSaveMsg("Error saving form: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ErrorBoundary>
      <Box maxWidth={900} mt={4}>
        <Typography variant="h5" sx={{ mb: 2 }}>Dynamic Form Builder</Typography>
        <TextField
          label="Form Name"
          value={formName}
          onChange={e => setFormName(e.target.value)}
          sx={{ mb: 2 }}
          fullWidth
        />
        {(formSchema && Array.isArray(formSchema.components)) ? (
          <FormBuilder
            form={{
              ...formSchema,
              components: formSchema.components
            }}
            onChange={schema => {
              setFormSchema({
                ...schema,
                components: Array.isArray(schema.components) ? schema.components : []
              });
            }}
          />
        ) : (
          <div style={{color:'red'}}>Invalid form schema</div>
        )}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="contained" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? 'Hide Preview' : 'Preview Form'}
          </Button>
          <Button variant="contained" color="success" onClick={handleSaveForm} disabled={saving}>
            {saving ? 'Saving...' : 'Save Form'}
          </Button>
        </Box>
        {showPreview && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Form Preview</Typography>
            {/* Custom preview for List structure */}
            {formSchema.components && Array.isArray(formSchema.components) && formData.List ? (
              <Box sx={{ background: '#fff', borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {formData.List.map((section, idx) => (
                  <Box key={idx} sx={{ mt: 3, mx: 3, background: '#f6f7fb', borderRadius: 3, border: '1.5px solid #8a1538', boxShadow: '0 1px 4px rgba(139,22,52,0.06)', p: 2.2 }}>
                    <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1.08rem', mb: 1.5 }}>{section.header}</Typography>
                    <Box>
                      {section.items && section.items.map((item, itemIdx) => {
                        // Text input if no options
                        if (Array.isArray(item.options) && item.options.length === 0) {
                          return (
                            <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.7, borderBottom: itemIdx !== section.items.length - 1 ? '1px solid #ececec' : 'none' }}>
                              <Typography sx={{ color: '#222', fontSize: '0.98rem', fontWeight: 500 }}>{item.title}</Typography>
                                {item.isSignature ? (
                                  item.value ? (
                                    <img
                                      src={item.value}
                                      alt="Signature"
                                      style={{ width: 180, height: 80, borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fafafa', objectFit: 'contain' }}
                                    />
                                  ) : (
                                    <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1.05rem' }}>No signature</Typography>
                                  )
                                ) : (
                                  <input
                                    type="text"
                                    value={item.value}
                                    readOnly
                                    style={{ width: 180, padding: '8px 12px', borderRadius: '7px', border: '1px solid #e0e0e0', fontSize: '1rem', background: '#fafafa', color: '#8a1538', fontWeight: 500 }}
                                  />
                                )}
                            </Box>
                          );
                        }
                        // Checkbox group for array value and options (even if only one option)
                        if (Array.isArray(item.value) && Array.isArray(item.options) && item.options.length > 0) {
                          return (
                            <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.7, borderBottom: itemIdx !== section.items.length - 1 ? '1px solid #ececec' : 'none' }}>
                              <Typography sx={{ color: '#222', fontSize: '0.98rem', fontWeight: 500 }}>{item.title}</Typography>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                {item.options.map((opt, oidx) => (
                                  <label key={oidx} style={{ color: '#8a1538', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontSize: '1.05rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={item.value.includes(opt)}
                                      readOnly
                                      style={{ accentColor: '#8a1538', marginRight: 6 }}
                                    />
                                    {typeof opt === 'string' ? opt.toUpperCase() : opt}
                                  </label>
                                ))}
                              </Box>
                            </Box>
                          );
                        }
                        // Radio group for string value and multiple options
                        const isRadio = Array.isArray(item.options) && item.options.length > 1 && typeof item.value === 'string';
                        const radioOptions = Array.isArray(item.options) && item.options.length > 0
                          ? item.options.map(opt => typeof opt === 'string' ? (opt.toUpperCase() === 'N/A' ? 'N/A' : opt.toUpperCase()) : opt)
                          : [];
                        if (isRadio) {
                          return (
                            <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.7, borderBottom: itemIdx !== section.items.length - 1 ? '1px solid #ececec' : 'none' }}>
                              <Typography sx={{ color: '#222', fontSize: '0.98rem', fontWeight: 500 }}>{item.title}</Typography>
                              <Box sx={{ display: 'flex', gap: 3 }}>
                                {radioOptions.map((opt, oidx) => (
                                  <label key={oidx} style={{ color: '#8a1538', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontSize: '1.05rem' }}>
                                    <input
                                      type="radio"
                                      name={item.title + itemIdx}
                                      value={opt}
                                      checked={typeof item.value === 'string' && item.value.toUpperCase() === opt}
                                      readOnly
                                      style={{ accentColor: '#8a1538', marginRight: 6 }}
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </Box>
                            </Box>
                          );
                        }
                        // Boolean checkbox (true/false)
                        if (item.options && item.options.length === 1 && (item.value === 'true' || item.value === true || item.value === 'false' || item.value === false)) {
                          return (
                            <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.7, borderBottom: itemIdx !== section.items.length - 1 ? '1px solid #ececec' : 'none' }}>
                              <Typography sx={{ color: '#222', fontSize: '0.98rem', fontWeight: 500 }}>{item.title}</Typography>
                              <input
                                type="checkbox"
                                checked={item.value === 'true' || item.value === true}
                                readOnly
                                style={{ accentColor: '#8a1538', marginRight: 6 }}
                              />
                            </Box>
                          );
                        }
                        // Fallback: show value as text
                        return (
                          <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.7, borderBottom: itemIdx !== section.items.length - 1 ? '1px solid #ececec' : 'none' }}>
                            <Typography sx={{ color: '#222', fontSize: '0.98rem', fontWeight: 500 }}>{item.title}</Typography>
                            <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1.05rem' }}>{String(item.value)}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Form
                form={formSchema}
                submission={{ data: formData }}
                onChange={e => setFormData(e.data)}
              />
            )}
          </Box>
        )}
        {saveMsg && (
          <Typography sx={{ mt: 2, color: saveMsg.includes('success') ? 'green' : 'red' }}>{saveMsg}</Typography>
        )}
      </Box>
    </ErrorBoundary>
  );
}
